import { useQuery } from '@tanstack/react-query';
import { Headset, Mail, Phone } from 'lucide-react';
import { api } from '@/api/client';

interface Contact { id: number; name: string; email: string; mobile: string | null }

/**
 * The customer-support person looking after this merchant.
 *
 * Every merchant has a named CS rep internally, but the merchant was never told who — so a question
 * went to a generic inbox and got routed by hand to the person who already knew the account. Putting
 * the name, email and number on screen turns that into a direct call.
 *
 * Renders NOTHING when no active CS is assigned: an empty "Your support contact" card with dashes in
 * it is worse than no card, because it reads as "nobody is looking after you".
 */
export function SupportContactCard({ compact }: { compact?: boolean }) {
  const { data } = useQuery<Contact | null>({
    queryKey: ['merchant', 'support-contact'],
    queryFn: async () => (await api.get('/merchant/support-contact')).data ?? null,
    staleTime: 30 * 60_000,
    retry: false,
  });

  if (!data?.name) return null;

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <Headset size={15} className="text-primary shrink-0" />
        <div className="leading-tight">
          {/* The title goes ABOVE the name: in the top bar a name on its own is just a name — it
              needs to say whose it is before it means anything. */}
          <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Your support manager</div>
          <div className="flex items-center gap-1.5 text-xs">
            <span className="font-semibold text-slate-800">{data.name}</span>
            {data.mobile && (
              <a href={`tel:${data.mobile}`} className="text-slate-500 hover:text-primary">· {data.mobile}</a>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-9 h-9 rounded-lg bg-primary-50 text-primary flex items-center justify-center shrink-0">
          <Headset size={18} />
        </span>
        <div className="min-w-0">
          <div className="text-xs text-slate-500">Your support manager</div>
          <div className="font-semibold text-slate-900 truncate">{data.name}</div>
        </div>
      </div>
      <div className="space-y-1.5 text-sm">
        {data.mobile && (
          <a href={`tel:${data.mobile}`} className="flex items-center gap-2 text-slate-700 hover:text-primary">
            <Phone size={14} className="text-slate-400 shrink-0" /> {data.mobile}
          </a>
        )}
        <a href={`mailto:${data.email}`} className="flex items-center gap-2 text-slate-700 hover:text-primary">
          <Mail size={14} className="text-slate-400 shrink-0" /> <span className="truncate">{data.email}</span>
        </a>
      </div>
    </div>
  );
}
